import type {
  MedicationPrescriptionStatus,
  PrescribedProductMetadataSchema,
  ReissueLinkAttributesSchema,
} from '@montugroup/prescription-contracts';
import { CircularProgress, Stack } from '@mui/material';
import { kebabCase } from 'lodash';
import { v4 as uuidv4 } from 'uuid';

import { useFeatureFlags, useGetFilteredProducts, useGetPrescribedProducts } from '@/hooks';
import { useAppStore } from '@/state-management';

import { ConfirmPrescriptionCard } from '../ConfirmPrescriptionCard/ConfirmPrescriptionCard';
import type { PatientDetails, PrescriberDetails } from '../ConfirmPrescriptionCard/ConfirmPrescriptionCard.types';
import { PrescribedMedicationHistory } from '../PrescribedMedicationHistory/PrescribedMedicationHistory';
import type { MedicationType } from '../ProductDetailsCard/ProductDetailsCard.types';

import { FF_PAT_PREFILL_DOSAGE, FF_PAT_PRESCRIPTION_DOSAGE_MIN_LENGTH_VALIDATION } from '@/constants/featureFlags';
import { mapPrescribedMedicationsToPreviousItems } from '@/state-management/slices/prescriptionAssistant/utils';
import { toast } from '@montugroup/design-system';
import { useEffect } from 'react';
import { usePrescriptionAssistantTracking } from '../../hooks/usePrescriptionAssistantTracking';
import { PrescriptionPad } from './PrescriptionPad';
import type { CancelMedicationItemType, MedicationPadItemType } from './PrescriptionPad.types';
import { PrescriptionPadStep } from './PrescriptionPad.types';
import { PrescriptionsReloadContent } from './PrescriptionsReloadComponents';

export type PrescriptionPadWrapperProps = {
  doctorId: number;
  patientId: string;
  prescriberNumber: string;
  doctorPatientDetails: {
    doctor: PrescriberDetails;
    patient: PatientDetails;
  };
  isConcessionCardHolder?: boolean;
};

export const PrescriptionPadWrapper = ({
  doctorId,
  patientId,
  prescriberNumber,
  doctorPatientDetails,
}: PrescriptionPadWrapperProps) => {
  const {
    prescriptionPad: {
      step: prescriptionPadStep,
      medicationPadItems,
      devicePadItems,
      actions: prescriptionPadReviewActions,
    },
    prescriptionHistory: { itemsToReissue, itemsToCancel, itemsToAction, previousItems },
  } = useAppStore.use.prescriptionAssistant();
  const updatePrescriptionPadStep = useAppStore.use.updatePrescriptionPadStep();
  const updatePrescriptionHistoryItemsToReissue = useAppStore.use.updatePrescriptionHistoryItemsToReissue();
  const updatePrescriptionHistoryItemsToCancel = useAppStore.use.updatePrescriptionHistoryItemsToCancel();
  const updatePrescriptionHistoryPreviousItems = useAppStore.use.updatePrescriptionHistoryPreviousItems();
  const updatePrescriptionHistoryItemsToAction = useAppStore.use.updatePrescriptionHistoryItemsToAction();
  const addReissuedMedicationToPrescriptionPad = useAppStore.use.addReissuedMedicationToPrescriptionPad();
  const removeMedicationPadItemById = useAppStore.use.removeMedicationPadItemById();
  const updatePrescriptionPadReviewAction = useAppStore.use.updatePrescriptionPadReviewAction();
  const updatePrescriptionPadDispenseNote = useAppStore.use.updatePrescriptionPadDispenseNote();
  const reloadPrescriptionAssitant = useAppStore.use.reloadPrescriptionAssitant();
  const isPrescriptionPadInSync = useAppStore.use.isPrescriptionPadInSync()();
  const { flags } = useFeatureFlags();
  const ffPatPrescriptionDosageMinLengthValidation = flags[FF_PAT_PRESCRIPTION_DOSAGE_MIN_LENGTH_VALIDATION];
  const ffPatPrefillDosage = flags[FF_PAT_PREFILL_DOSAGE];
  const hasCompletedActions = itemsToAction.length === 0;

  const { isLoading: isFilteredProductsLoading, allProducts } = useGetFilteredProducts({
    doctorId,
    isConcession: doctorPatientDetails?.patient?.hasValidConcessionCard,
  });

  const { data: prescribedProductsData } = useGetPrescribedProducts({
    patientId,
  });

  const { trackSelectMedicationReissue, trackSelectMedicationCancel } = usePrescriptionAssistantTracking();

  const handlePrescriptionPadReload = () => {
    reloadPrescriptionAssitant();
    toast.dismiss('prescription-pad-reload');
  };

  // store previous prescribed items on refetch
  useEffect(() => {
    if (prescribedProductsData?.body) {
      updatePrescriptionHistoryPreviousItems(
        mapPrescribedMedicationsToPreviousItems(prescribedProductsData.body.prescribedMedications),
      );
    }
  }, [prescribedProductsData?.body]);

  // Show a toast notification if the prescription pad is out of sync
  useEffect(() => {
    if (!isPrescriptionPadInSync) {
      toast.warning(
        {
          message: <PrescriptionsReloadContent reload={handlePrescriptionPadReload} />,
        },
        {
          toastId: 'prescription-pad-reload',
          autoClose: false,
          closeButton: false,
        },
      );
    } else {
      toast.dismiss('prescription-pad-reload');
    }
  }, [isPrescriptionPadInSync]);

  const handleReturnToScriptDetails = () => {
    updatePrescriptionPadStep(PrescriptionPadStep.Create);
  };

  const medicationPadItemDetails = medicationPadItems
    .filter((item) => !!item.medicationPadItem)
    .map((item) => item.medicationPadItem) as MedicationType[];

  const removeIdFromItemsToAction = (productId: number) => {
    if (!itemsToAction.length) {
      return;
    }

    updatePrescriptionHistoryItemsToAction(itemsToAction.filter((itemToActionId) => itemToActionId !== productId));
  };

  /**
   * Handles the event when the reissue link is clicked. Note that this function is
   * memoised by the DataGrid component, so state changes in the parent will not
   * be accessible.
   */
  const handleReissueProductClick = (
    attributes: ReissueLinkAttributesSchema,
    medication: PrescribedProductMetadataSchema,
    prescriptionStatus: MedicationPrescriptionStatus,
  ) => {
    if (attributes.productId) {
      removeIdFromItemsToAction(attributes.productId);
      trackSelectMedicationReissue(attributes.productId);
    }

    // skip if the medication is already in the medication pad items
    if (medicationPadItems.find((item) => item.medicationPadItem?.productId === attributes.productId)) {
      return;
    }

    const mappedFilters: [string, string][] = attributes.filters.map((filter) => {
      const formatLabel = kebabCase(filter.label);
      return [`${formatLabel}`, `${filter.value}`];
    });
    const filters = new Map([...mappedFilters]);

    const reissueStatus = previousItems.find((item) => item.id === attributes.productId)?.reissueStatus;

    const reissuedMedication: MedicationPadItemType = {
      medicationPadItemId: uuidv4(),
      medicationPadItem: {
        productId: attributes.productId || 0,
        productName: medication.productName,
        description: medication.description,
        supplierName: medication.supplierName,
      },
      filters: filters,
      reissueStatus: {
        isReissued: true,
        ...reissueStatus,
      },
      prescriptionStatus,
    };
    // remove the reissued medication from the cancel list
    updatePrescriptionHistoryItemsToCancel(
      itemsToCancel.filter((item) => item.productId !== reissuedMedication?.medicationPadItem?.productId),
    );
    updatePrescriptionHistoryItemsToReissue([...itemsToReissue, reissuedMedication]);
    addReissuedMedicationToPrescriptionPad(allProducts, reissuedMedication, {
      ffPatPrescriptionDosageMinLengthValidation,
      ffPatPrefillDosage,
    });
  };

  const handleCancelMedicationItemClick = (cancelItem: CancelMedicationItemType) => {
    // remove productId from items to action
    removeIdFromItemsToAction(cancelItem.productId);
    trackSelectMedicationCancel(cancelItem.productId);

    // skip if the medication is already in the cancel list
    if (itemsToCancel.find((item) => item.productId === cancelItem.productId)) {
      return;
    }

    const medicationPadItem = medicationPadItems.find(
      (item) => item.medicationPadItem?.productId === cancelItem.productId,
    );

    const medicationPadItemId = medicationPadItem?.medicationPadItemId;

    if (medicationPadItemId) {
      removeMedicationPadItemById(medicationPadItemId);
    } else {
      updatePrescriptionHistoryItemsToCancel([...itemsToCancel, cancelItem]);
    }
  };

  const handleDispenseNoteChange = (dispenseNote: string) => {
    updatePrescriptionPadDispenseNote(dispenseNote);
  };

  const renderLoading = () => (
    <Stack direction="row" alignItems="center" justifyContent="center" height={300}>
      <CircularProgress color="inherit" />
    </Stack>
  );

  const renderPrescriptionPad = () => {
    if (!hasCompletedActions) {
      return null;
    }

    return (
      <PrescriptionPad
        doctorId={doctorId}
        isConcessionCardHolder={doctorPatientDetails?.patient?.hasValidConcessionCard}
      />
    );
  };

  return isFilteredProductsLoading ? (
    renderLoading()
  ) : (
    <>
      {prescriptionPadStep === PrescriptionPadStep.Create && (
        <>
          <PrescribedMedicationHistory
            patientId={patientId}
            onClickReissue={handleReissueProductClick}
            onClickCancel={handleCancelMedicationItemClick}
          />
          {renderPrescriptionPad()}
        </>
      )}
      {prescriptionPadStep === PrescriptionPadStep.Confirm && (
        <ConfirmPrescriptionCard
          prescriberNumber={prescriberNumber}
          prescriberDetails={doctorPatientDetails?.doctor}
          patientDetails={doctorPatientDetails?.patient}
          createdPrescriptionDetails={medicationPadItemDetails}
          onReturn={handleReturnToScriptDetails}
          createdDevices={devicePadItems}
          reviewActions={prescriptionPadReviewActions}
          onActionClick={updatePrescriptionPadReviewAction}
          onDispenseNoteChange={handleDispenseNoteChange}
        />
      )}
    </>
  );
};
